import type { ISourceDocument } from '@/types/chat-session-types'
import { logger } from '@/utils/logger'

export interface IParsedChunk {
  type: 'content' | 'metadata' | 'error' | 'done'
  content?: string
  isReplacement?: boolean
  error?: string
  sourceDocuments?: ISourceDocument[]
  followUpQuestions?: string[]
  answerFound?: boolean
  reasoning?: string
  referenceNotes?: string[]
}

export interface IStreamMetadata {
  sourceDocuments?: ISourceDocument[]
  followUpQuestions?: string[]
  reasoning?: string
  answerFound?: boolean
  referenceNotes?: string[]
}

export interface IFinalizedResponse {
  success: boolean
  answer?: string
  reasoning?: string
  answerFound?: boolean
  referenceNotes?: string[]
  sourceDocuments?: ISourceDocument[]
  usedConversationHistory?: boolean
}

/**
 * Parses a single SSE streaming chunk into a structured result.
 * Pure function — no side effects or store dependencies.
 */
export function parseStreamChunk(chunk: string): IParsedChunk {
  if (chunk === '[DONE]' || chunk === '"[DONE]"') {
    return { type: 'done' }
  }

  try {
    if (chunk.includes('"error"')) {
      const errorData = JSON.parse(chunk)
      if (errorData.error) {
        return { type: 'error', error: errorData.error }
      }
    }

    const chunkData = JSON.parse(chunk)
    const result: IParsedChunk = { type: 'metadata' }

    // Extract content from OpenAI-style format (case-insensitive)
    const dataObj = chunkData.data || chunkData.Data || chunkData
    const choices = dataObj.choices || dataObj.Choices
    const choice = choices?.[0]
    const delta = choice?.delta || choice?.Delta
    const message = choice?.message || choice?.Message

    const contentSource = delta || message
    if (choices && choices.length > 0 && contentSource) {
      const content = contentSource.content || contentSource.Content
      if (content) {
        result.type = 'content'
        result.content = content
        result.isReplacement = !!message && !delta
      } else {
        logger.warn(
          'Choice exists but no content field',
          { delta, message },
          { component: 'parseStreamChunk' }
        )
      }
    } else if (chunkData.content) {
      result.type = 'content'
      result.content = chunkData.content
      result.isReplacement = false
    } else if (
      !chunkData.answerFound &&
      chunkData.answerFound === undefined &&
      !chunkData.sourceDocuments &&
      !chunkData.followUpQuestions &&
      !chunkData.reasoning &&
      !chunkData.referenceNotes
    ) {
      logger.warn(
        'No content found in chunk',
        { chunkData },
        { component: 'parseStreamChunk' }
      )
    }

    // Extract metadata fields
    if (chunkData.answerFound !== undefined) {
      result.answerFound = chunkData.answerFound
    }
    if (chunkData.referenceNotes) {
      result.referenceNotes = chunkData.referenceNotes
    }
    if (chunkData.sourceDocuments) {
      result.sourceDocuments = chunkData.sourceDocuments
    }
    if (chunkData.followUpQuestions) {
      result.followUpQuestions = chunkData.followUpQuestions
    }
    if (chunkData.reasoning) {
      result.reasoning = chunkData.reasoning
    }

    return result
  } catch {
    logger.warn(
      'Failed to parse streaming chunk',
      { chunk },
      { component: 'parseStreamChunk' }
    )
    return { type: 'metadata' }
  }
}

/**
 * Parses the accumulated JSON content after streaming completes.
 * Merges with metadata received during streaming (which takes precedence).
 * Pure function — no side effects.
 */
export function finalizeStreamResponse(
  accumulatedJson: string,
  streamMetadata: IStreamMetadata
): IFinalizedResponse {
  try {
    const parsed = JSON.parse(accumulatedJson)

    return {
      success: true,
      answer: parsed.answer,
      reasoning: parsed.reasoning,
      answerFound: parsed.answer_found ?? false,
      referenceNotes: parsed.reference_notes || [],
      sourceDocuments:
        streamMetadata.sourceDocuments || parsed.source_documents || [],
      usedConversationHistory: parsed.used_conversation_history ?? false,
    }
  } catch {
    logger.error(
      'Failed to parse final JSON response',
      { accumulatedContent: accumulatedJson },
      { component: 'finalizeStreamResponse' }
    )

    return {
      success: true,
      answerFound: false,
      referenceNotes: [],
      sourceDocuments: streamMetadata.sourceDocuments || [],
      usedConversationHistory: false,
    }
  }
}
